import React, { useState, useEffect } from 'react';
import { CreditCard, X } from 'lucide-react';
import { MerchantInvoice } from '../../types/gst';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoice: MerchantInvoice | null;
  onConfirmPayment: (
    paymentAmount: number,
    paymentMode: 'CASH' | 'UPI' | 'NET_BANKING' | 'CHEQUE',
    paymentRefNumber: string,
    paymentDate: string
  ) => Promise<void>;
  isSubmitting: boolean;
}

export const RecordPaymentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  invoice,
  onConfirmPayment,
  isSubmitting
}) => {
  if (!isOpen || !invoice) return null;

  const [paymentAmount, setPaymentAmount] = useState<number>(invoice.balanceDue > 0 ? invoice.balanceDue : 0);
  const [paymentMode, setPaymentMode] = useState<'CASH' | 'UPI' | 'NET_BANKING' | 'CHEQUE'>('UPI');
  const [paymentRefNumber, setPaymentRefNumber] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (invoice) {
      setPaymentAmount(invoice.balanceDue > 0 ? invoice.balanceDue : 0);
    }
  }, [invoice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentAmount <= 0) return;
    await onConfirmPayment(paymentAmount, paymentMode, paymentRefNumber, paymentDate);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#131316] p-6 shadow-2xl space-y-4 text-xs">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-400" />
            <div>
              <h3 className="font-bold text-white text-sm">Record Customer Payment</h3>
              <p className="text-[11px] text-slate-400">Invoice #{invoice.invoiceNumber} &bull; {invoice.customerName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Balances Card */}
        <div className="grid grid-cols-3 gap-2 rounded-xl border border-white/10 bg-[#0e1015] p-3 text-center">
          <div>
            <div className="text-[9px] uppercase font-bold text-slate-500">Invoice Total</div>
            <div className="font-mono font-bold text-white text-xs mt-0.5">₹{invoice.grandTotal.toLocaleString('en-IN')}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase font-bold text-slate-500">Already Paid</div>
            <div className="font-mono font-bold text-emerald-400 text-xs mt-0.5">₹{(invoice.paidAmount || 0).toLocaleString('en-IN')}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase font-bold text-slate-500">Balance Due</div>
            <div className="font-mono font-bold text-amber-400 text-xs mt-0.5">₹{invoice.balanceDue.toLocaleString('en-IN')}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
              Payment Amount to Collect (₹) *
            </label>
            <input
              type="number"
              min="1"
              max={invoice.balanceDue}
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(Number(e.target.value))}
              className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white font-mono font-bold text-sm focus:outline-none focus:border-emerald-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Payment Mode *
              </label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as any)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-2 text-white text-xs focus:outline-none focus:border-emerald-500"
              >
                <option value="UPI">UPI (GPay, PhonePe, Paytm)</option>
                <option value="NET_BANKING">Bank Transfer (NEFT/RTGS)</option>
                <option value="CASH">Cash</option>
                <option value="CHEQUE">Cheque</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Payment Date
              </label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-2.5 py-2 text-white text-xs focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
              Reference / UTR / Cheque Number
            </label>
            <input
              type="text"
              value={paymentRefNumber}
              onChange={(e) => setPaymentRefNumber(e.target.value)}
              placeholder="e.g. UPI Ref / UTR / Cheque # 889912"
              className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white font-mono text-xs focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold transition shadow-sm"
            >
              {isSubmitting ? 'Saving...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
